import is from '@sindresorhus/is';
import { AnimatePresence } from 'framer-motion';
import type { FunctionComponent, ReactNode } from 'react';
import { Fragment, memo, useEffect, useState } from 'react';

import type { JobRole } from '~/shared/data-contract/job-role';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { ShakeAnimation } from '~/ui/animations/shake/ShakeAnimation';
import { useShakeOnClickOutside } from '~/ui/animations/shake/use-shake-on-click-outside';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Tile, TileContent } from '~/ui/core/tile/Tile';
import { TileFooter } from '~/ui/core/tile/TileFooter';
import { theme } from '~/ui/theme/cobrainer-theme';

import { Backdrop, ContentContainer } from '../components/Common';
import { ErrorContent } from '../components/ErrorContent';
import { JSA_PRIMARY_DETAIL_TILE_SLOT_KEY } from '../url-tile-payloads';
import { COMMENTS_RAIL_TRANSITION, COMMENTS_RAIL_WIDTH_PX } from './comments/constants';
import { DetailsContent } from './DetailsContent';
import { LoadingContent } from './LoadingContent';
import { useGetActiveJobProfileVersion } from './use-get-active-job-profile-version';

export type AvailableJobItems = {
    readonly architectureId: string;
    readonly clusterId: string;
    readonly roles: readonly JobRole[];
};

type DetailsProps = {
    readonly architectureId: string;
    readonly commentsAside?: (params: { readonly commentResolutionDisabled: boolean }) => ReactNode;
    readonly isArchived?: boolean;
    readonly isCommentsOpen?: boolean;
    readonly isOpen: boolean;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly jobFamilyId?: string;
    readonly mode: 'edit' | 'view';
    readonly onActiveLanguageChange?: (language: LanguageCodeForJSA) => void;
    readonly onClose: () => void;
    readonly onToggleComments?: () => void;
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly version?: number;
};

const DetailsImpl: FunctionComponent<DetailsProps> = ({
    architectureId,
    commentsAside,
    isArchived,
    isCommentsOpen = false,
    isOpen = true,
    itemId,
    itemType = 'job-role',
    jobFamilyId,
    mode,
    onActiveLanguageChange,
    onClose,
    onToggleComments,
    preferredLanguage,
    version,
}) => {
    const { activeVersion, error, isLoading, item, setActiveVersion } = useGetActiveJobProfileVersion(
        itemId,
        itemType,
        architectureId,
        is.number(version) ? version.toString() : undefined,
    );
    const [isEditing, setIsEditing] = useState(mode === 'edit');
    const { isShaking, shake } = useShakeOnClickOutside();

    // biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration
    useEffect(() => {
        setIsEditing(mode === 'edit');
    }, [mode, itemId]);

    if (isLoading) {
        return (
            <Tile className="[&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:h-full">
                <ScrollArea>
                    <TileContent>
                        <ContentContainer>
                            <LoadingContent
                                tileKey={`${JSA_PRIMARY_DETAIL_TILE_SLOT_KEY}.${architectureId}.${itemId}`}
                            />
                        </ContentContainer>
                    </TileContent>
                </ScrollArea>
            </Tile>
        );
    }

    if (error !== null || is.nullOrUndefined(item)) {
        return (
            <Tile className="[&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:h-full">
                <ScrollArea>
                    <TileContent>
                        <ContentContainer>
                            <ErrorContent error={error} onClose={onClose} />
                        </ContentContainer>
                    </TileContent>
                </ScrollArea>
            </Tile>
        );
    }

    return (
        <AnimatePresence mode="wait">
            {isOpen && (
                <Fragment>
                    {isEditing && (
                        <Backdrop
                            animate={{
                                opacity: '0.4',
                            }}
                            initial={{
                                backgroundColor: theme.colors.gray[200],
                                opacity: '0',
                            }}
                            onClick={shake}
                        />
                    )}
                    <ShakeAnimation isShaking={isShaking}>
                        <Tile
                            className="[&_[data-radix-scroll-area-viewport]>div]:block! [&_[data-radix-scroll-area-viewport]>div]:h-full"
                            data-testid="job-profile-details"
                        >
                            <ScrollArea>
                                <div className="flex min-h-full w-full items-start">
                                    <div className="flex min-w-0 flex-1 self-stretch flex-col">
                                        <TileContent>
                                            <ContentContainer>
                                                {is.undefined(item.jobProfile) === false && (
                                                    <DetailsContent
                                                        key={item.id}
                                                        architectureId={architectureId}
                                                        isArchived={isArchived}
                                                        isCommentsOpen={isCommentsOpen}
                                                        isEditing={isEditing}
                                                        item={item}
                                                        itemId={item.id}
                                                        itemType={itemType}
                                                        jobFamilyId={jobFamilyId}
                                                        onActiveLanguageChange={onActiveLanguageChange}
                                                        parentRoleId={
                                                            itemType === 'job-level' ? item.parentId : undefined
                                                        }
                                                        permissions={item.actions}
                                                        preferredLanguage={preferredLanguage}
                                                        profile={item.jobProfile}
                                                        selectedVersion={activeVersion}
                                                        setActiveVersion={setActiveVersion}
                                                        setIsEditing={setIsEditing}
                                                        onClose={onClose}
                                                        onToggleComments={onToggleComments}
                                                    />
                                                )}
                                                <div className="mt-auto ">
                                                    <TileFooter />
                                                </div>
                                            </ContentContainer>
                                        </TileContent>
                                    </div>
                                    <div
                                        aria-hidden={!isCommentsOpen}
                                        className="shrink-0 overflow-x-clip overflow-y-visible"
                                        inert={!isCommentsOpen}
                                        style={{
                                            flexBasis: isCommentsOpen ? `${COMMENTS_RAIL_WIDTH_PX}px` : '0px',
                                            transition: COMMENTS_RAIL_TRANSITION,
                                            width: isCommentsOpen ? `${COMMENTS_RAIL_WIDTH_PX}px` : '0px',
                                        }}
                                    >
                                        {commentsAside?.({ commentResolutionDisabled: isEditing })}
                                    </div>
                                </div>
                            </ScrollArea>
                        </Tile>
                    </ShakeAnimation>
                </Fragment>
            )}
        </AnimatePresence>
    );
};

export const Details = memo(DetailsImpl);
